
import { useState, useCallback, useEffect, forwardRef, useImperativeHandle } from "react";
import { Rule, RuleCondition, RuleAction } from "@/lib/types";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { UseFormReturn } from "react-hook-form";
import { ConditionBuilder, type ConditionType } from "./conditions/ConditionBuilder";
import { validateScheduleValue } from "@/lib/schedule";
import { ActionBuilder } from "./actions/ActionBuilder";
import { Plus, Trash, AlertTriangle, ArrowUp, ArrowDown } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import * as z from "zod";

export const formSchema = z.object({
  name: z.string().min(1, "Rule name is required"),
  enabled: z.boolean(),
});

export type FormValues = z.infer<typeof formSchema>;

export interface RuleFormRef {
  validateAndAttemptSubmit: () => boolean;
}

export interface RuleFormProps {
  initialData?: Partial<Rule>;
  tenantId: string;
  mailboxId: string;
  form: UseFormReturn<FormValues>;
  conditions: RuleCondition[];
  onConditionsChange: (conditions: RuleCondition[]) => void;
  actions: RuleAction[];
  onActionsChange: (actions: RuleAction[]) => void;
  onSubmit?: (data: Omit<Rule, "id" | "created_at" | "updated_at">) => Promise<void>;
  onValidationChange?: (isValid: boolean) => void;
}

// Returns a copy of `arr` with the items at `a` and `b` exchanged.
function swap<T>(arr: T[], a: number, b: number): T[] {
  const next = [...arr];
  [next[a], next[b]] = [next[b], next[a]];
  return next;
}

function ReorderButtons({
  index,
  count,
  label,
  onMove,
}: {
  index: number;
  count: number;
  label: string;
  onMove: (direction: -1 | 1) => void;
}) {
  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Move ${label} up`}
        disabled={index === 0}
        onClick={() => onMove(-1)}
      >
        <ArrowUp className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label={`Move ${label} down`}
        disabled={index === count - 1}
        onClick={() => onMove(1)}
      >
        <ArrowDown className="h-4 w-4" />
      </Button>
    </>
  );
}

export const RuleForm = forwardRef<RuleFormRef, RuleFormProps>(({
  initialData,
  tenantId,
  mailboxId,
  form,
  conditions,
  onConditionsChange: setConditions,
  actions,
  onActionsChange: setActions,
  onSubmit,
  onValidationChange
}, ref) => {
  const [formErrors, setFormErrors] = useState<string[]>([]);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);

  // Stable per-item ids for React list keys, kept separate from the persisted
  // condition/action data so the ConditionBuilder / ActionBuilder subtrees (open
  // edit panels, fetched templates, half-typed input) stay attached to the right
  // item when the list is reordered or an earlier item is removed.
  const [conditionIds, setConditionIds] = useState<string[]>(() => conditions.map(() => crypto.randomUUID()));
  const [actionIds, setActionIds] = useState<string[]>(() => actions.map(() => crypto.randomUUID()));

  const handleAddCondition = () => {
    setConditions([...conditions, { type: "subject_includes_words", value: [] }]);
    setConditionIds([...conditionIds, crypto.randomUUID()]);
  };

  const handleUpdateCondition = (index: number, condition: RuleCondition) => {
    const newConditions = [...conditions];
    newConditions[index] = condition;
    setConditions(newConditions);
  };

  const handleRemoveCondition = (index: number) => {
    const newConditions = [...conditions];
    newConditions.splice(index, 1);
    setConditions(newConditions);

    const newConditionIds = [...conditionIds];
    newConditionIds.splice(index, 1);
    setConditionIds(newConditionIds);
  };

  // Move both arrays together so each key stays glued to its item.
  const handleMoveCondition = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= conditions.length) return;

    setConditions(swap(conditions, index, target));
    setConditionIds(swap(conditionIds, index, target));
  };

  const handleAddAction = () => {
    setActions([...actions, { type: "reply", value: { templateId: "", saveToSentItems: false } }]);
    setActionIds([...actionIds, crypto.randomUUID()]);
  };

  const handleUpdateAction = (index: number, action: RuleAction) => {
    const newActions = [...actions];
    newActions[index] = action;
    setActions(newActions);
  };

  const handleRemoveAction = (index: number) => {
    const newActions = [...actions];
    newActions.splice(index, 1);
    setActions(newActions);

    const newActionIds = [...actionIds];
    newActionIds.splice(index, 1);
    setActionIds(newActionIds);
  };

  // Move both arrays together so each key stays glued to its item.
  const handleMoveAction = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= actions.length) return;

    setActions(swap(actions, index, target));
    setActionIds(swap(actionIds, index, target));
  };

  // One schedule condition per rule: offer "schedule" only if no *other*
  // condition in the list already is one.
  const disabledTypesFor = (index: number): ConditionType[] =>
    conditions.some((c, i) => i !== index && c.type === "schedule") ? ["schedule"] : [];

  const validateForm = useCallback(() => {
    const errors: string[] = [];
    
    // Validate actions
    if (actions.length === 0) {
      errors.push("At least one action is required");
    }
    
    // Validate each action
    actions.forEach((action, index) => {
      if (action.type === "reply" && (!action.value?.templateId)) {
        errors.push("Template selection is required for Reply action");
      }
      
      if (action.type === "forward" && (!action.value?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(action.value.email))) {
        errors.push("Valid email address is required for Forward action");
      }
    });
    
    // Validate each condition if there are any
    if (conditions.length > 0) {
      conditions.forEach((condition, index) => {
        if ((condition.type === "subject_includes_words" || condition.type === "subject_matches_patterns") && 
            (!condition.value || !Array.isArray(condition.value) || condition.value.length === 0)) {
          errors.push(`Subject condition ${index + 1}: At least one item is required`);
        }
        
        if (condition.type === "sender_match_patterns") {
          if (!condition.value || !Array.isArray(condition.value) || condition.value.length === 0) {
            errors.push(`Sender patterns condition ${index + 1}: At least one pattern is required`);
          }
        }
        if (condition.type === "schedule") {
          validateScheduleValue(condition.value).forEach((problem) => {
            errors.push(`Schedule condition ${index + 1}: ${problem}`);
          });
        }
      });
      // disabledTypes only guards the Select; a rule loaded from the database
      // can already carry two.
      if (conditions.filter((c) => c.type === "schedule").length > 1) {
        errors.push("Only one schedule condition is allowed per rule");
      }
    }
    
    // Only show errors if user has attempted to submit
    if (hasAttemptedSubmit) {
      setFormErrors(errors);
    }
    
    const isValid = errors.length === 0;
    
    // Notify parent component about validation state change
    if (onValidationChange) {
      onValidationChange(isValid);
    }
    
    return isValid;
  }, [conditions, actions, hasAttemptedSubmit, onValidationChange]);

  const handleFormSubmit = async (data: FormValues) => {
    setHasAttemptedSubmit(true);
    
    if (!validateForm()) {
      // If there are errors, don't submit
      return;
    }

    if (onSubmit) {
      const ruleData: Omit<Rule, "id" | "created_at" | "updated_at"> = {
        tenant_id: tenantId,
        mailbox_id: mailboxId,
        name: data.name,
        order: initialData?.order || 0,
        enabled: data.enabled,
        conditions: conditions,
        actions: actions
      };
      await onSubmit(ruleData);
    }
  };

  // Expose validateAndAttemptSubmit through ref
  useImperativeHandle(ref, () => ({
    validateAndAttemptSubmit: () => {
      setHasAttemptedSubmit(true);
      return validateForm();
    }
  }));

  // Run validation whenever conditions or actions change
  useEffect(() => {
    validateForm();
  }, [validateForm]);

  return (
    <>
      <Form {...form}>
        <form id="rule-form" className="space-y-6" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Rule Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter rule name" {...field} />
                  </FormControl>
                  <FormDescription>
                    Give your rule a descriptive name
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Rule Status</FormLabel>
                    <FormDescription>
                      Enable or disable this rule
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium">Conditions</h3>
                {conditions.length === 0 && (
                  <p className="text-xs text-muted-foreground">
                    No conditions means this rule will apply to all emails
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddCondition}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Condition
              </Button>
            </div>

            <div className="space-y-3">
              {conditions.map((condition, index) => (
                <div key={conditionIds[index]} className="border rounded-md p-4 bg-card">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Condition {index + 1}</h4>
                    <div className="flex items-center">
                      <ReorderButtons
                        index={index}
                        count={conditions.length}
                        label="condition"
                        onMove={(direction) => handleMoveCondition(index, direction)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveCondition(index)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <ConditionBuilder
                    condition={condition}
                    onChange={(updatedCondition) => handleUpdateCondition(index, updatedCondition)}
                    disabledTypes={disabledTypesFor(index)}
                  />
                </div>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Actions</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleAddAction}
              >
                <Plus className="h-4 w-4 mr-1" /> Add Action
              </Button>
            </div>

            <div className="space-y-3">
              {actions.length === 0 && (
                <div className="p-4 border rounded-md bg-muted/50 text-center text-muted-foreground">
                  No actions added. Add at least one action.
                </div>
              )}

              {actions.map((action, index) => (
                <div key={actionIds[index]} className="border rounded-md p-4 bg-card">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium">Action {index + 1}</h4>
                    <div className="flex items-center">
                      <ReorderButtons
                        index={index}
                        count={actions.length}
                        label="action"
                        onMove={(direction) => handleMoveAction(index, direction)}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveAction(index)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <ActionBuilder
                    action={action}
                    onChange={(updatedAction) => handleUpdateAction(index, updatedAction)}
                  />
                </div>
              ))}
            </div>
          </div>
          
          {formErrors.length > 0 && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                <div className="font-semibold mb-1">Please fix the following errors:</div>
                <ul className="list-disc pl-5 space-y-1">
                  {formErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </AlertDescription>
            </Alert>
          )}
          
          {/* Hidden submit button for compatibility with external form submissions */}
          {onSubmit && (
            <input type="submit" id="rule-form-submit-button" hidden />
          )}
        </form>
      </Form>
    </>
  );
});
